import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Save } from "lucide-react";
import { AuthCardLayout } from "@/components/layout/AuthCardLayout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { changePassword } from "@/lib/api/auth";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";
import { useSession } from "@/lib/session/SessionContext";
import { homeFor } from "@/lib/session/homeRoute";
import type { BootstrapResponse } from "@/lib/types/session";

type Sitzung = NonNullable<BootstrapResponse["session"]>;

/** Dieselbe Sitzung, aber ohne das gerade erfüllte mustChangePassword-Flag. */
function erledigt(session: Sitzung): Sitzung {
  if (session.actorType === "admin") return { ...session, user: { ...session.user, mustChangePassword: false } };
  if (session.actorType === "member") return { ...session, mustChangePassword: false };
  return session;
}

export function ChangePasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const { alert } = useDialog();
  const { run, pending } = useAsyncAction();
  const { session, refetch } = useSession();
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      if (password !== confirmation) throw new Error("Die Passwörter stimmen nicht überein.");
      await changePassword(password);
      await refetch();
      await alert("Passwort erfolgreich geändert.", { title: "Passwort geändert" });
      // Ziel aus homeRoute.ts statt fest "/admin" (S1.6): hier landen seit dieser
      // Session auch Mitglieder, und deren Startseite hängt an ihrer Rolle. Das
      // Flag ist nach dem Speichern weg, wird hier aber selbst gelöscht statt
      // auf die frisch geladene Sitzung zu warten — sonst schickte homeFor
      // direkt wieder hierher zurück.
      navigate(session ? homeFor(erledigt(session)) : "/login", { replace: true });
    });
  };

  return (
    <AuthCardLayout>
      <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
        <KeyRound className="size-5" aria-hidden="true" /> Passwort ändern (Erstanmeldung)
      </h3>
      <p className="mb-6 text-sm leading-[1.4] text-muted-foreground">
        {session?.actorType === "member"
          ? "Dein aktuelles Passwort stammt aus der Übergabe deines Unternehmens. Bitte vergib jetzt dein eigenes."
          : "Dein aktuelles Passwort ist ein Standardpasswort (111111). Bitte ändere dieses vor dem ersten Zugriff ab."}
      </p>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="new-admin-pass">Neues Passwort</FieldLabel>
            <Input
              id="new-admin-pass"
              type="password"
              placeholder="Mindestens 12 Zeichen"
              required
              minLength={12}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="new-admin-pass-confirm">Neues Passwort bestätigen</FieldLabel>
            <Input
              id="new-admin-pass-confirm"
              type="password"
              placeholder="Passwort wiederholen"
              required
              minLength={12}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          <Button type="submit" className="mt-2 w-full hover:bg-primary-dark" disabled={pending}>
            Passwort speichern <Save data-icon className="size-4" aria-hidden="true" />
          </Button>
        </FieldGroup>
      </form>
    </AuthCardLayout>
  );
}
